import type { AdminCategory, AdminFlag, AdminFlagResolution } from "./types";
import { req } from "./transport";

export const moderationApi = {
  // Batch item 10: user warnings
  async adminWarnUser(id: number, reason: string, note?: string) {
    return req<{ warning: any }>(`/admin/users/${id}/warn`, { method: "POST", body: JSON.stringify({ reason, note }) });
  },

  async adminUserWarnings(id: number) {
    const r = await req<{ warnings: any[] }>(`/admin/users/${id}/warnings`);
    return r?.warnings ?? [];
  },

  async adminCategories(group?: string) {
    const r = await req<{ categories: AdminCategory[] }>(`/admin/categories${group ? `?group=${encodeURIComponent(group)}` : ""}`, {}, { rejectUnauthorized: true });
    return r?.categories ?? [];
  },

  async adminAddCategory(name: string, group?: string) {
    return req<{ category: AdminCategory }>("/admin/categories", { method: "POST", body: JSON.stringify({ name, group }) });
  },

  async adminUpdateCategory(id: string, patch: { name?: string; active?: boolean; orderIndex?: number }) {
    return req<{ category: AdminCategory }>(`/admin/categories/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(patch) });
  },

  async adminDeleteCategory(id: string) {
    return req<{ ok: boolean }>(`/admin/categories/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  // Increment 38: risk / fraud flags
  async reportFlag(input: { targetType: string; targetId: string; targetUserId?: number; reason: string; detail?: string }) {
    return req<{ ok: boolean; flagged: boolean; id: string | null; deduped: boolean }>("/flags", { method: "POST", body: JSON.stringify(input) });
  },

  async adminFlags(status = "open") {
    const r = await req<{ status: string; flags: AdminFlag[] }>(`/admin/flags?status=${encodeURIComponent(status)}`, {}, { rejectUnauthorized: true });
    return r.flags;
  },

  async adminFlagStats() {
    return req<Record<string, number>>("/admin/flags/stats", {}, { rejectUnauthorized: true });
  },

  async adminResolveFlag(id: string, resolution: AdminFlagResolution, note?: string) {
    return req<{ ok: boolean; id: string; status: string; resolution: AdminFlagResolution; applied: string }>(`/admin/flags/${encodeURIComponent(id)}/resolve`, { method: "POST", body: JSON.stringify({ resolution, note }) }, { rejectUnauthorized: true });
  },

  // Increment 12: Ratings & Reviews
  async submitReview(payload: { dealType: "order" | "contract"; dealId: string; rating: number; comment?: string }) {
    return req<{ review: any }>("/reviews", { method: "POST", body: JSON.stringify(payload) });
  },

  async userReviews(userId: number, category?: "jobseeker" | "jobs" | "services" | "projects" | "companies" | "rfqs" | "marketplace") {
    const query = category ? `?category=${encodeURIComponent(category)}` : "";
    return req<{ average: number; count: number; breakdown: Record<string, number>; reviews: any[] }>(`/reviews/user/${userId}${query}`);
  },

  async myReviews(signal?: AbortSignal) {
    return req<{ average: number; count: number; breakdown: Record<string, number>; received: any[]; written: any[] }>("/reviews/me", { signal }, { rejectUnauthorized: signal !== undefined });
  },

  async pendingReviews(signal?: AbortSignal) {
    const r = await req<{ pending: any[] }>("/reviews/pending", { signal }, { rejectUnauthorized: signal !== undefined });
    if (signal && !Array.isArray(r?.pending)) throw new Error("Missing pending reviews.");
    return r?.pending ?? [];
  }
};
